import { ColorSpectrum } from "../Pages/Data/Visualize/DataReview/Types/ColorSpectrum";
import { DSADisplayScale, isQEEGDSASourceModality } from "../Pages/Data/Visualize/DataReview/Types/AnalysisDetails";
import { DEFAULT_SPECTROGRAM_TRACE_SETTINGS, RenderStrategy } from "../Pages/Data/Visualize/DataReview/Types/Trace";
import type { SummaryValueConfig } from "../Pages/Data/Visualize/DataReview/Types/Graph";

type LabelSetting = {
    name: string
    min: number
    max: number
    color: string
    autoscale: boolean
    // Digits after the decimal point in the measurement's numeric display by default, per the
    // Supported Measurement and Window Matrix (FRM-11-06): 1 for temperatures, 2 for ECG, Resp,
    // PRx and COx, whole numbers otherwise.
    decimals: number
    lowerBound?: number
    upperBound?: number
    colorSpectrum?: ColorSpectrum
    displayScale?: DSADisplayScale
}

type MaybeLabelSetting = {
    name?: string
    min?: number
    max?: number
    lowerBound?: number
    upperBound?: number
    color?: string
    autoscale?: boolean
    colorSpectrum?: ColorSpectrum
    displayScale?: DSADisplayScale
}

type WithRenderStrategy = {
    renderStrategy: RenderStrategy
}


export const LABEL_SETTINGS: Record<string, LabelSetting> = {
    "ABP_Dias": {'name': 'Diastolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ABP_Mean": {'name': 'Mean ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ABP_na": {'name': 'ABP', "min": 0, "max": 250, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ABP_Syst": {'name': 'Systolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART_Dias": {'name': 'Diastolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART_Mean": {'name': 'Mean ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART_na": {'name': 'ABP', "min": 0, "max": 250, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART_Syst": {'name': 'Systolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART1_Dias": {'name': 'Diastolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART1_Mean": {'name': 'Mean ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART1_na": {'name': 'ABP', "min": 0, "max": 250, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "ART1_Syst": {'name': 'Systolic ABP', "min": 0, "max": 200, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "BIS_na": {'name': 'BIS', "min": 0, "max": 100, 'color': '#000000', 'autoscale': true, 'decimals': 0},
    "CO2_na": {'name': 'CO2', "min": 0, "max": 100, 'color': '#000000', 'autoscale': true, 'decimals': 0},
    "CPP_na": {'name': 'CPP', "min": 40, "max": 120, 'color': '#2600BD', 'autoscale': true, 'decimals': 0},
    "CPP2_na": {'name': 'CPP', "min": 40, "max": 120, 'color': '#2600BD', 'autoscale': true, 'decimals': 0},
    "CVP_Mean": {'name': 'CVP', "min": 0, "max": 20, 'color': '#000000', 'autoscale': true, 'decimals': 0},
    "CVP_na": {'name': 'CVP', "min": 0, "max": 20, 'color': '#000000', 'autoscale': true, 'decimals': 0},
    "ECG_II": {'name': 'ECG II', "min": -0.5, "max": 2, 'color': '#49B500', 'autoscale': false, 'decimals': 2},
    "EtCO2_na": {'name': 'EtCO2', "min": 15, "max": 100, 'color': '#009F4E', 'autoscale': true, 'decimals': 0},
    "HR_na": {'name': 'HR', "min": 30, "max": 240, 'color': '#C37400', 'autoscale': true, 'decimals': 0},
    "IC1_Mean": {'name': 'Mean ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "IC1_na": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "IC2_Mean": {'name': 'Mean ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "IC2_na": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP_Mean": {'name': 'Mean ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP_na": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP1_Dias": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP1_Mean": {'name': 'Mean ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP1_na": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP1_Syst": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP2_Dias": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP2_Mean": {'name': 'Mean ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP2_na": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "ICP2_Syst": {'name': 'ICP', "min": 0, "max": 100, 'color': '#46B300', 'autoscale': true, 'decimals': 0},
    "LAP_Mean": {'name': 'Systolic LAP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "NBP_Dias": {'name': 'Diastolic NBP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "NBP_Mean": {'name': 'Mean NBP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "NBP_Syst": {'name': 'Systolic NBP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "P_Mean": {'name': 'Mean P', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "P_na": {'name': 'P', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "P1_Mean": {'name': 'P1', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "P1_na": {'name': 'P1', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "PAP_Dias": {'name': 'Diastolic PAP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "PAP_Mean": {'name': 'Mean PAP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "PAP_Syst": {'name': 'Systolic PAP', "min": 0, "max": 100, 'color': '#FF0000', 'autoscale': true, 'decimals': 0},
    "PbtO2_na": {'name': 'PbtO2', "min": 0, "max": 100, 'color': '#7C00B3', 'autoscale': true, 'decimals': 0},
    "PLETH_na": {'name': 'Pleth', "min": 0, "max": 100, 'color': '#7F00D0', 'autoscale': true, 'decimals': 0},
    "RESP_na": {'name': 'Resp', "min": -0.5, "max": 1, 'color': '#7F00D0', 'autoscale': true, 'decimals': 2},
    "RR_na":  {'name': 'RR', "min": 18, "max": 25, 'color': '#62A978', 'autoscale': true, 'decimals': 0},
    "SpO2_na": {'name': 'SpO2', "min": 85, "max": 100, 'color': '#8E7500', 'autoscale': true, 'decimals': 0},
    "T1_na": {'name': 'T1', "min": 0, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "T2_na": {'name': 'T2', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "Tcereb_na": {'name': 'Tcereb', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "Tcore_na": {'name': 'Tcore', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "Temp_na": {'name': 'Temp', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "Tesop_na": {'name': 'Tesop', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "Tskin_na": {'name': 'Tskin', "min": 30, "max": 42, 'color': '#FF8E74', 'autoscale': true, 'decimals': 1},
    "UVP_Mean": {'name': 'UVP', "min": 0, "max": 100, 'color': '#7C00B3', 'autoscale': true, 'decimals': 0},
    "UVP_na": {'name': 'UVP', "min": 0, "max": 100, 'color': '#7C00B3', 'autoscale': true, 'decimals': 0},

    // Analytics
    "CPPOpt": {'name': "CPPopt", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "CPPOpt_LLA": {'name': "CPPopt LLA", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "CPPOpt_ULA": {'name': "CPPopt ULA", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "CPPOpt (multi-window)": {'name': "CPPopt", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "CPPOpt_LLA (multi-window)": {'name': "CPPopt LLA", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "CPPOpt_ULA (multi-window)": {'name': "CPPopt ULA", "min": 40, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt": {'name': "MAPopt", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt_LLA": {'name': "MAPopt LLA", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt_ULA": {'name': "MAPopt ULA", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt (multi-window)": {'name': "MAPopt", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt_LLA (multi-window)": {'name': "MAPopt LLA", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "MAPOpt_ULA (multi-window)": {'name': "MAPopt ULA", "min": 20, "max": 120, 'color': "#FF7043", 'autoscale': false, 'decimals': 0},
    "PRx": {'name': "PRx", "min": -1, "max": 1, "lowerBound": 1, "upperBound": -1, "color": "#FF7043", "colorSpectrum": ColorSpectrum.RED_YELLOW_GREEN, "autoscale": false, 'decimals': 2},
    "COx": {'name': "COx", "min": 1, "max": 0, "lowerBound": 1, "upperBound": 0, "color": "#FF7043", "colorSpectrum": ColorSpectrum.RED_YELLOW_GREEN, "autoscale": false, 'decimals': 2},
    "COIN": {'name': "COIN", "min": -100, "max": 0, "color": "#FF7043", "autoscale": false, 'decimals': 0},

    "qEEG_DSA": {'name': "qEEG DSA", "min": 0, "max": 20, 'color': "#FF7043", 'autoscale': false, 'decimals': 0, ...DEFAULT_SPECTROGRAM_TRACE_SETTINGS},
}

function getRenderStrategy(key: string | undefined, renderStrategy: RenderStrategy) {
    switch (renderStrategy) {
        case RenderStrategy.LINE:
        case RenderStrategy.LINE_STEP:
            return key?.toLowerCase().startsWith("pupillary") ? RenderStrategy.LINE_STEP : renderStrategy
        default:
            return renderStrategy
    }
}

export function applyModalitySpecificDefaults<T extends WithRenderStrategy>(record: T, key: string | undefined, renderStrategy: RenderStrategy = RenderStrategy.LINE): T & MaybeLabelSetting {
    let result: T & MaybeLabelSetting = {
        min: undefined,
        max: undefined,
        color: undefined,
        autoscale: undefined,
        colorSpectrum: undefined,
        displayScale: undefined,
        lowerBound: undefined,
        upperBound: undefined,
        ...record
    }

    result.renderStrategy = getRenderStrategy(key, renderStrategy)

    if (!key) {
        return result
    }

    // Source DSA keys carry the channel group (`qEEG_DSA_LeftHemisphere`), so they
    // share the analytic's settings by prefix rather than by exact key -- but only
    // for spectrograms, so legacy line/heatmap configs keep their old (no-defaults)
    // behavior.
    const defaults = LABEL_SETTINGS[key]
        ?? (result.renderStrategy === RenderStrategy.SPECTROGRAM && isQEEGDSASourceModality(key) ? LABEL_SETTINGS["qEEG_DSA"] : undefined)

    if (defaults === undefined) {
        return result
    }

    // `decimals` describes the numeric display, not the trace, so it is not copied onto it.
    const { decimals: _decimals, ...defaultsCopy } = defaults
    Object.assign(result, defaultsCopy)

    if (renderStrategy === RenderStrategy.HEATMAP) {
        result.lowerBound = defaultsCopy.lowerBound ?? defaultsCopy.min
        result.upperBound = defaultsCopy.upperBound ?? defaultsCopy.max
        result.colorSpectrum = defaultsCopy.colorSpectrum ?? ColorSpectrum.INFERNO
    }

    if (renderStrategy === RenderStrategy.SPECTROGRAM) {
        result.displayScale = defaultsCopy.displayScale
        result.colorSpectrum = defaultsCopy.colorSpectrum ?? ColorSpectrum.DISCRETE_RGB
        result.lowerBound = defaultsCopy.lowerBound
        result.upperBound = defaultsCopy.upperBound
    }

    return result
}

// How many decimals a measurement's number shows by default. A measurement that is not in
// LABEL_SETTINGS shows whole numbers, as before.
export function getDefaultDecimals(key: string | undefined): number {
    return (key !== undefined ? LABEL_SETTINGS[key]?.decimals : undefined) ?? 0
}

type WithNumericDisplay = {
    name: string
    rawDataKey?: string
    color?: unknown
    summaryValueConfig?: SummaryValueConfig
}

// The numeric display a measurement starts with when it is chosen in Configure Graphs: its default
// decimals, not shown until the user ticks Visible (a waveform's own number is rarely what is
// wanted: next to ECG it is usually HR). A row switched to another measurement takes the new
// one's decimals and keeps whether its number was shown.
export function withDefaultNumericDisplay<T extends WithNumericDisplay>(trace: T, previous?: SummaryValueConfig): T & { summaryValueConfig: SummaryValueConfig } {
    return {
        ...trace,
        summaryValueConfig: {
            name: trace.name,
            color: typeof trace.color === "string" ? trace.color : "black",
            precision: getDefaultDecimals(trace.rawDataKey),
            isVisible: previous?.isVisible ?? false,
        },
    }
}
